<script setup>
// Profissionais, grade, bloqueios e regras de marcação (só administradores; ver AGENDA.md).
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AgendaPageLayout from '../components/AgendaPageLayout.vue';
import ProfessionalDialog from '../components/ProfessionalDialog.vue';
import BlocksDialog from '../components/BlocksDialog.vue';
import agendaAPI from 'dashboard/api/agenda';
import { useAgendaData } from '../composables/useAgendaData';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

const { t } = useI18n();
const {
  setting,
  professionals,
  hasLoaded,
  fetchAll,
  removeProfessional,
} = useAgendaData();

const professionalDialog = ref(null);
const blocksDialog = ref(null);
const deleteDialog = ref(null);
const professionalToDelete = ref(null);
const rules = ref({ min_notice_hours: '2', horizon_days: '30', options_per_offer: '3' });
const isSavingRules = ref(false);

const sortedProfessionals = computed(() =>
  [...professionals.value].sort(
    (a, b) =>
      Number(b.active) - Number(a.active) ||
      a.specialty.localeCompare(b.specialty) ||
      a.name.localeCompare(b.name)
  )
);

const integerIn = (value, min, max) => {
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max;
};

const rulesValid = computed(
  () =>
    integerIn(rules.value.min_notice_hours, 0, 168) &&
    integerIn(rules.value.horizon_days, 1, 365) &&
    integerIn(rules.value.options_per_offer, 1, 6)
);

// "Seg 08:00–12:00, 14:00–18:00 · Qua 08:00–12:00"
const gridSummary = professional =>
  WEEKDAYS.map(day => {
    const periods = professional.working_periods.filter(p => p.weekday === day);
    if (!periods.length) return null;
    const hours = periods.map(p => `${p.start}–${p.end}`).join(', ');
    return `${t(`AGENDA.WEEKDAYS_SHORT.${day}`)} ${hours}`;
  })
    .filter(Boolean)
    .join(' · ');

watch(
  setting,
  value => {
    rules.value = {
      min_notice_hours: String(Math.round(value.min_notice_minutes / 60)),
      horizon_days: String(value.horizon_days),
      options_per_offer: String(value.options_per_offer),
    };
  },
  { immediate: true }
);

const saveRules = async () => {
  if (!rulesValid.value) return;
  isSavingRules.value = true;
  try {
    const { data } = await agendaAPI.updateSetting({
      min_notice_minutes: Number(rules.value.min_notice_hours) * 60,
      horizon_days: Number(rules.value.horizon_days),
      options_per_offer: Number(rules.value.options_per_offer),
    });
    setting.value = data;
    useAlert(t('AGENDA.PROFESSIONALS.RULES.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('AGENDA.PROFESSIONALS.RULES.ERROR'));
  } finally {
    isSavingRules.value = false;
  }
};

const askDelete = professional => {
  professionalToDelete.value = professional;
  deleteDialog.value?.open();
};

const confirmDelete = async () => {
  const professional = professionalToDelete.value;
  deleteDialog.value?.close();
  if (!professional) return;
  try {
    await agendaAPI.deleteProfessional(professional.id);
    removeProfessional(professional.id);
    useAlert(t('AGENDA.PROFESSIONALS.DELETED'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('AGENDA.PROFESSIONALS.LOAD_ERROR'));
  }
};

onMounted(async () => {
  try {
    await fetchAll();
  } catch {
    useAlert(t('AGENDA.PROFESSIONALS.LOAD_ERROR'));
  }
});
</script>

<template>
  <AgendaPageLayout
    :title="t('AGENDA.PROFESSIONALS.TITLE')"
    :description="t('AGENDA.PROFESSIONALS.DESCRIPTION')"
  >
    <template #actions>
      <Button
        :label="t('AGENDA.PROFESSIONALS.NEW')"
        icon="i-lucide-user-plus"
        size="sm"
        data-testid="agenda-new-professional"
        @click="professionalDialog?.open()"
      />
    </template>

    <div v-if="!hasLoaded" class="flex justify-center py-16">
      <Spinner />
    </div>

    <div v-else class="flex flex-col gap-6 pb-10">
      <div
        v-if="!professionals.length"
        class="flex flex-col items-center gap-2 px-6 py-12 text-center rounded-xl outline-1 outline-dashed outline-n-weak"
      >
        <span class="i-lucide-stethoscope size-8 text-n-slate-9" />
        <h2 class="text-heading-2 text-n-slate-12">
          {{ t('AGENDA.PROFESSIONALS.EMPTY_TITLE') }}
        </h2>
        <p class="text-body-para text-n-slate-11 max-w-md">
          {{ t('AGENDA.PROFESSIONALS.EMPTY_DESCRIPTION') }}
        </p>
      </div>

      <ul v-else class="flex flex-col gap-3" data-testid="agenda-professionals-list">
        <li
          v-for="professional in sortedProfessionals"
          :key="professional.id"
          class="flex flex-wrap items-start gap-4 px-5 py-4 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
          :class="{ 'opacity-60': !professional.active }"
        >
          <span
            class="mt-1.5 size-3 rounded-full shrink-0"
            :style="{ backgroundColor: professional.color }"
          />
          <div class="flex flex-col flex-1 min-w-[16rem] gap-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-heading-3 text-n-slate-12">
                {{ professional.name }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ professional.specialty }}
              </span>
              <span
                v-if="!professional.active"
                class="px-2 py-0.5 rounded-md text-label-small bg-n-slate-3 text-n-slate-11"
              >
                {{ t('AGENDA.PROFESSIONALS.INACTIVE') }}
              </span>
            </div>
            <span class="text-label-small text-n-slate-11">
              {{ t('AGENDA.PROFESSIONALS.DURATION', { minutes: professional.duration_minutes }) }}
            </span>
            <span class="text-label-small text-n-slate-11 tabular-nums">
              {{ gridSummary(professional) || t('AGENDA.PROFESSIONALS.NO_GRID') }}
            </span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <Button
              :label="t('AGENDA.PROFESSIONALS.EDIT')"
              icon="i-lucide-pencil"
              variant="ghost"
              color="slate"
              size="sm"
              @click="professionalDialog?.open(professional)"
            />
            <Button
              :label="t('AGENDA.PROFESSIONALS.BLOCKS')"
              icon="i-lucide-calendar-off"
              variant="ghost"
              color="slate"
              size="sm"
              @click="blocksDialog?.open(professional)"
            />
            <Button
              :label="t('AGENDA.PROFESSIONALS.DELETE')"
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="sm"
              @click="askDelete(professional)"
            />
          </div>
        </li>
      </ul>

      <form
        class="flex flex-col gap-4 px-5 py-4 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
        data-testid="agenda-rules-form"
        @submit.prevent="saveRules"
      >
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">
            {{ t('AGENDA.PROFESSIONALS.RULES.TITLE') }}
          </span>
          <span class="text-body-para text-n-slate-11">
            {{ t('AGENDA.PROFESSIONALS.RULES.DESCRIPTION') }}
          </span>
        </div>
        <div class="grid gap-3 sm:grid-cols-3">
          <Input
            v-model="rules.min_notice_hours"
            type="number"
            max="168"
            :label="t('AGENDA.PROFESSIONALS.RULES.MIN_NOTICE')"
          />
          <Input
            v-model="rules.horizon_days"
            type="number"
            max="365"
            :label="t('AGENDA.PROFESSIONALS.RULES.HORIZON')"
          />
          <Input
            v-model="rules.options_per_offer"
            type="number"
            max="6"
            :label="t('AGENDA.PROFESSIONALS.RULES.OPTIONS')"
          />
        </div>
        <div class="flex flex-wrap items-center justify-between gap-3">
          <span class="text-label-small text-n-slate-11">
            {{ t('AGENDA.PROFESSIONALS.RULES.TIMEZONE', { timezone: setting.timezone }) }}
          </span>
          <Button
            type="submit"
            :label="t('AGENDA.PROFESSIONALS.RULES.SAVE')"
            icon="i-lucide-check"
            size="sm"
            :disabled="!rulesValid"
            :is-loading="isSavingRules"
          />
        </div>
      </form>
    </div>

    <ProfessionalDialog ref="professionalDialog" />
    <BlocksDialog ref="blocksDialog" />
    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="t('AGENDA.PROFESSIONALS.DELETE_DIALOG.TITLE')"
      :description="
        t('AGENDA.PROFESSIONALS.DELETE_DIALOG.DESCRIPTION', {
          name: professionalToDelete?.name || '',
        })
      "
      :confirm-button-label="t('AGENDA.PROFESSIONALS.DELETE_DIALOG.CONFIRM')"
      :cancel-button-label="t('AGENDA.PROFESSIONALS.DELETE_DIALOG.CANCEL')"
      @confirm="confirmDelete"
    />
  </AgendaPageLayout>
</template>
